import { useQuery } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportTicket } from '../../models/Support/ISupportTicket';
import { TicketPriority, TicketStatus } from '../../models/Support/SupportEnums';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS, type SupportTicketSearchParams } from './supportQueryHelpers';

interface UseSupportTicketSearchArgs {
	query: string;
	mailboxId?: string;
	accountId?: string;
	status?: TicketStatus;
	priority?: TicketPriority;
	categoryId?: string;
	assigneeId?: string;
	csmId?: string;
	page?: number;
	pageSize?: number;
}

/**
 * Search support tickets by free-text query and/or structured qualifiers.
 *
 * The query runs against the server's full-text index over customer email, subject, and message
 * content — spanning open, closed, and older tickets. Qualifier params add exact id/enum filters
 * that narrow the results. The query is disabled until there is either >= 2 characters of free
 * text or at least one qualifier, so transient single keystrokes do not fire a request and the
 * backend's "at least one parameter" guard is never hit.
 */
export function useSupportTicketSearchQuery(params: UseSupportTicketSearchArgs) {
	const {
		query,
		mailboxId,
		accountId,
		status,
		priority,
		categoryId,
		assigneeId,
		csmId,
		page = 1,
		pageSize = 25,
	} = params;
	const userSession = useUserSession();

	const hasQualifiers = !!(status || priority || categoryId || assigneeId || csmId);
	const hasFreeText = !!query && query.length >= 2;
	const isEnabled = hasFreeText || hasQualifiers;

	const searchParams: SupportTicketSearchParams = {
		query: query || undefined,
		mailboxId,
		accountId,
		status,
		priority,
		categoryId,
		assigneeId,
		csmId,
		page,
		pageSize,
	};

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.search(searchParams),
		staleTime: 10_000,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IPagedCollection<ISupportTicket>>(
				SUPPORT_PATHS.tickets.search(searchParams),
				'GET'
			),
		enabled: isEnabled,
		meta: { toastErrorMessage: 'Failed to search tickets' },
	});
}
